// People: presence from day logs, the roster grouped by team, picker lists, and invitations (ports of
// Team+Store.swift, SyncReplica.updatePresence (GroundTeam/Sync/SyncReplica.swift:415), and SyncCenter.inviteMessage /
// formatInviteCode (GroundTeam/Sync/SyncCenter.swift:1261-1297); normalize_invite_code from
// supabase/migrations/20260929180000_groundteam_self_signup.sql:113). WEB_PLAN.md §3.10, §5.2, §5.4.

import { USER_ROLE_TITLES } from '@/contracts/enums';
import type { ActivityPayload, DayLogPayload, TeamPayload, UserPayload } from '@/contracts/models';
import type { Role, TeamInvitation } from '@/contracts/wire';
import { parseWireDate } from '@/contracts/wireDate';
import { effectiveMs } from './derived';
import type { Coordinate } from './geo';
import { descendantTeamIds } from './visibility';
import { byId, compareLocalized, fullName, idKey, idSet, lookup, ms, sameId, trimAll } from './util';

export { fullName, initials, userName } from './util';

// MARK: - Presence

export type Presence = 'online' | 'away' | 'offline';

/** Online within 15 minutes, Away within 8 hours (`User.presenceTitle`). */
export const PRESENCE_ONLINE_MS = 15 * 60 * 1000;
export const PRESENCE_AWAY_MS = 8 * 60 * 60 * 1000;

export const PRESENCE_TITLES: Readonly<Record<Presence, string>> = { online: 'Online', away: 'Away', offline: 'Offline' };

/** Presence from the last known position time (none → offline; a future time counts as now). */
export function presenceOf(lastSeenAt: Date | null | undefined, now: Date = new Date()): Presence {
  if (!lastSeenAt) return 'offline';
  const age = Math.max(0, now.getTime() - lastSeenAt.getTime());
  if (age < PRESENCE_ONLINE_MS) return 'online';
  if (age < PRESENCE_AWAY_MS) return 'away';
  return 'offline';
}

/** A rep's last known position: the newest breadcrumb across their day logs. */
export interface RepPosition {
  userId: string;
  coordinate: Coordinate;
  at: Date;
  presence: Presence;
}

/**
 * `SyncReplica.updatePresence`: per user (lower-case id), the newest `points[].timestamp` over all their day logs.
 * iOS skips the device's own user (its GPS is fresher); the console has no GPS, so everyone is included.
 */
export function lastPositions(dayLogs: readonly DayLogPayload[], now: Date = new Date()): Map<string, RepPosition> {
  const latest = new Map<string, { userId: string; coordinate: Coordinate; t: number }>();
  for (const log of dayLogs) {
    let best: { coordinate: Coordinate; t: number } | undefined;
    for (const p of log.points) {
      const t = ms(p.timestamp);
      if (t === undefined) continue;
      if (!best || t > best.t) best = { coordinate: p.coordinate, t };
    }
    if (!best) continue;
    const key = idKey(log.userId);
    const current = latest.get(key);
    if (current && current.t >= best.t) continue;
    latest.set(key, { userId: log.userId, coordinate: best.coordinate, t: best.t });
  }
  const out = new Map<string, RepPosition>();
  for (const [key, v] of latest) {
    const at = new Date(v.t);
    out.set(key, { userId: v.userId, coordinate: { latitude: v.coordinate.latitude, longitude: v.coordinate.longitude }, at, presence: presenceOf(at, now) });
  }
  return out;
}

// MARK: - Roster and pickers

export interface RosterGroup {
  /** Undefined for the "No team" group (no `teamId`, or a team that no longer exists). */
  team?: TeamPayload;
  users: UserPayload[];
}

const byFullName = (a: UserPayload, b: UserPayload) => compareLocalized(fullName(a), fullName(b));

/** `teamRoster`: visible ACTIVE users grouped by team, teams by name, people by name, "No team" last. */
export function teamRoster(visibleUsers: readonly UserPayload[], teams: readonly TeamPayload[]): RosterGroup[] {
  const people = visibleUsers.filter((u) => u.isActive);
  const teamIds = idSet(people.map((u) => u.teamId));
  const teamsById = byId(teams);
  const groups: RosterGroup[] = teams
    .filter((t) => teamIds.has(idKey(t.id)))
    .sort((a, b) => compareLocalized(a.name, b.name))
    .map((team) => ({ team, users: people.filter((u) => sameId(u.teamId, team.id)).sort(byFullName) }));
  const orphans = people.filter((u) => !u.teamId || !lookup(teamsById, u.teamId)).sort(byFullName);
  if (orphans.length > 0) groups.push({ users: orphans });
  return groups;
}

/** `pickerUsers`: visible active users by name (owner pickers, filters). */
export function pickerUsers(visibleUsers: readonly UserPayload[]): UserPayload[] {
  return visibleUsers.filter((u) => u.isActive).sort(byFullName);
}

/** `members(of:)`: a team's active members (direct only), by name. */
export function teamMembers(users: readonly UserPayload[], teamId: string): UserPayload[] {
  return users.filter((u) => u.isActive && sameId(u.teamId, teamId)).sort(byFullName);
}

/** `managerCandidates(excluding:)`: active admins and managers, not the person themself, by name. */
export function managerCandidates(users: readonly UserPayload[], excludingId?: string | null): UserPayload[] {
  return users.filter((u) => u.isActive && u.role !== 'rep' && !sameId(u.id, excludingId)).sort(byFullName);
}

/** `parentCandidates(for:)`: teams that can be a parent (not the team itself or below it), by name. */
export function parentTeamCandidates(teams: readonly TeamPayload[], teamId?: string | null): TeamPayload[] {
  const blocked = idSet([...descendantTeamIds(teams, teamId), teamId]);
  return teams.filter((t) => !blocked.has(idKey(t.id))).sort((a, b) => compareLocalized(a.name, b.name));
}

/** `isEmailTaken(_:excluding:)`: another profile already uses this email (trimmed, case-insensitive). */
export function isEmailTaken(users: readonly UserPayload[], email: string, excludingId?: string | null): boolean {
  const e = trimAll(email).toLowerCase();
  if (e === '') return false;
  return users.some((u) => !sameId(u.id, excludingId) && u.email.toLowerCase() === e);
}

/** `lastActivity(for:)` for everyone at once: each user's newest completed activity (lower-case user id). */
export function lastActivityByUser(activities: readonly ActivityPayload[]): Map<string, ActivityPayload> {
  const out = new Map<string, { a: ActivityPayload; t: number }>();
  for (const a of activities) {
    if (a.status !== 'completed' || !a.userId) continue;
    const t = effectiveMs(a);
    const key = idKey(a.userId);
    const cur = out.get(key);
    if (!cur || t > cur.t) out.set(key, { a, t });
  }
  return new Map([...out].map(([k, v]) => [k, v.a]));
}

/** Leaderboard "Team only" helper: the team and every team below it (lower-case ids). */
export function teamTreeIds(teams: readonly TeamPayload[], teamId: string | null | undefined): Set<string> {
  return teamId ? idSet([teamId, ...descendantTeamIds(teams, teamId)]) : new Set();
}

// MARK: - Invitations

/** `SyncCenter.appStoreURL`. */
export const APP_STORE_URL = 'https://apps.apple.com/app/id6817090143';
/** The console's origin (invite links point at its `/welcome` page). */
export const WEB_APP_URL = 'https://groundteam.netlify.app';
export const INVITE_CODE_LENGTH = 12;

/** `SyncCenter.formatInviteCode`: "abcdefghjkmn" → "ABCD-EFGH-JKMN" (ASCII letters and digits, groups of four). */
export function formatInviteCode(code: string): string {
  const chars = Array.from(code.toUpperCase()).filter((c) => /^[A-Z0-9]$/.test(c));
  const groups: string[] = [];
  for (let i = 0; i < chars.length; i += 4) groups.push(chars.slice(i, i + 4).join(''));
  return groups.join('-');
}

/**
 * `public.normalize_invite_code`: upper-case, keep A–Z and 0–9, O→0, I→1, L→1; the 12-character code, or null when
 * the input has another length (the server answers `invalid_code` then).
 */
export function normalizeInviteCode(input: string | null | undefined): string | null {
  const v = (input ?? '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
  return v.length === INVITE_CODE_LENGTH ? v : null;
}

/**
 * `NoCompanyView.cleanCodeInput`: what the code field shows while typing: ASCII letters and digits upper-cased (at
 * most 12) plus the dashes people type; once all 12 are there, the grouped "XXXX-XXXX-XXXX" form.
 */
export function cleanInviteCodeInput(raw: string): string {
  let kept = '';
  let count = 0;
  for (const ch of raw.toUpperCase()) {
    if (/^[A-Z0-9]$/.test(ch)) {
      if (count >= INVITE_CODE_LENGTH) break;
      kept += ch;
      count += 1;
    } else if (ch === '-') {
      kept += ch;
    }
  }
  return count === INVITE_CODE_LENGTH ? formatInviteCode(kept) : kept;
}

/** The `/welcome` link that pre-fills a code (in the fragment, never the query: the code works like a password). */
export function inviteLink(code: string): string {
  return `${WEB_APP_URL}/welcome#code=${formatInviteCode(code)}`;
}

/** Reads `#code=…` from a location hash ("" / null when there is none). */
export function inviteCodeFromHash(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const code = params.get('code');
  return code ? cleanInviteCodeInput(code) : null;
}

/** Sign-in options of the iPhone app, for the invitation text (`SyncCenter.authMethodsList`). */
export interface AppAuthMethods {
  email?: boolean;
  phone?: boolean;
  apple?: boolean;
  google?: boolean;
}

/**
 * `authMethodsList`: "email", "email or Apple", "email, Apple or Google". Defaults are the app's before it knows the
 * server's settings (email and Apple on, phone and Google off); Google only shows beside Apple (App Store 4.8).
 */
export function authMethodsList(methods: AppAuthMethods = {}): string {
  const list: string[] = [];
  const apple = methods.apple ?? true;
  if (methods.email ?? true) list.push('email');
  if (methods.phone ?? false) list.push('phone');
  if (apple) list.push('Apple');
  if ((methods.google ?? false) && apple) list.push('Google');
  const last = list.pop();
  if (last === undefined) return 'email';
  return list.length === 0 ? last : `${list.join(', ')} or ${last}`;
}

export interface InviteMessageOptions {
  /** The company's name (settings `companyName`, else the membership's); "our team" when empty. */
  companyName?: string | null;
  authMethods?: AppAuthMethods;
  /** Locale of the expiry date ("October 15, 2026" in en-US). */
  locale?: string;
  /** Append the console link (default true). Without it the text is exactly the iPhone's. */
  includeWebLink?: boolean;
}

/**
 * `SyncCenter.inviteMessage(for:)`, word for word, plus (by default) a last paragraph with the console link
 * `https://groundteam.netlify.app/welcome#code=…` when the invitation has a code.
 */
export function inviteMessage(
  invitation: Pick<TeamInvitation, 'role' | 'email' | 'code' | 'expires_at'>,
  opts: InviteMessageOptions = {},
): string {
  const company = opts.companyName && trimAll(opts.companyName) !== '' ? trimAll(opts.companyName) : 'our team';
  const role = USER_ROLE_TITLES[invitation.role as Role] ?? invitation.role;
  const article = /^[AEIOU]/.test(role) ? 'an' : 'a';
  const address = invitation.email ? trimAll(invitation.email).toLowerCase() || null : null;
  const code = invitation.code ? formatInviteCode(invitation.code) || null : null;
  const lines = [
    `You're invited to join ${company} on Ground Team as ${article} ${role}.`,
    '',
    `1. Install Ground Team: ${APP_STORE_URL}`,
    `2. Create an account or sign in (${authMethodsList(opts.authMethods)}).`,
  ];
  if (code) lines.push(`3. Tap "Have an invite code?" and enter ${code}.`);
  else if (address) lines.push(`3. Use ${address} for your account. The invitation shows up by itself.`);
  else lines.push('3. Tap "Have an invite code?" and enter the code your admin sends you.');
  const notes: string[] = [];
  if (code && address) notes.push(`Signing up with ${address}? The invitation shows up by itself.`);
  const expires = parseWireDate(invitation.expires_at);
  if (expires) {
    const when = new Intl.DateTimeFormat(opts.locale, { dateStyle: 'long' }).format(expires);
    notes.push(`${code ? 'The code' : 'The invitation'} expires on ${when}.`);
  }
  if (notes.length > 0) lines.push('', ...notes);
  if (code && opts.includeWebLink !== false) lines.push('', `On a computer? Join at ${inviteLink(code)}`);
  return lines.join('\n');
}
